import { ReactNode, SyntheticEvent, useEffect, useMemo, useState } from 'react';
import { generatePath, useLocation, useNavigate } from 'react-router';
import { useParams } from 'react-router-dom';
import dayjs, { Dayjs } from 'dayjs';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import {
  Typography,
  Tab,
  Tabs,
  Divider,
  Container,
  Stack,
  Box,
  TextField,
  Checkbox,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import {
  PreviewTitleBox,
  StyledDatePicker,
  StyledNotes,
  StyledNoticeStack,
  StyledLink,
  StyledTypeCell,
} from './ActionPlan.styles';
import { useSelectedItemsValidation } from '../../hooks';
import {
  KpiBadge,
  ControlBar,
  IControlButton,
  MainContainer,
  FormGrid,
  FormItemLabel,
  IFieldValues,
  TableComponent,
  SendFormModal,
  useNotification,
  Loader,
} from '../../components';
import {
  ActionPlanGroupsTitle,
  ActionPlanGroupsTitleDescription,
  blocksOrder,
  InitiativeColumns,
  KpiColumns,
  PlanTabsTitle,
} from './types';
import URLs from '../URLs';
import { useInitiativesStore, useSalonStore } from '../../store';
import { Initiative } from '../../types/Initiative';
import { useTranslation } from 'react-i18next';
import { convertToSnakeCase, LocalizedColumns } from '../../utils';
import {
  ActionGuestPlan,
  ActionKPI,
  ActionPlanCount,
  BenchmarkItemValues,
  KpiItemValues,
  KpiStatus,
  KpiTitle,
  SalonActionPlan,
} from '../../types';
import { quarterMonths } from '../Salon/types';
import { KpiValues, SalonKpiValues } from '../KpiResults/types';

interface TabPanelProps {
  children?: ReactNode;
  index: number;
  value: number;
}

const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && <MainContainer>{children}</MainContainer>}
    </div>
  );
};

interface IDueDatePicker {
  disabled: boolean;
  date: Dayjs | null;
  setDate: (date: Dayjs) => void;
}

const DueDatePicker = ({ disabled, date, setDate }: IDueDatePicker) => {
  const [isError, setIsError] = useState(false);
  const { pathname } = useLocation();
  const maxDate = dayjs().add(2, 'year');

  useEffect(() => {
    setIsError(dayjs().subtract(1, 'day').isAfter(date));
  }, [date]);

  return (
    <StyledDatePicker
      disabled={disabled}
      onChange={(e) => setDate(e)}
      isError={!pathname.includes('completed') && isError}
      value={date}
      disablePast
      format={'MMM-DD-YYYY'}
      maxDate={maxDate}
      minDate={dayjs()}
      slotProps={{
        openPickerButton: { color: 'primary' },
        inputAdornment: { position: 'start' },
        textField: {
          variant: 'standard',
          error: isError,
          placeholder: 'MMM-DD-YYYY',
        },
      }}
    />
  );
};

export const ActionPlan = () => {
  const { t } = useTranslation();
  const [isSendModalOpen, setIsSendModalOpen] = useState(false);
  const { selectedItems, isMinSelected, isMaxSelected, onCheckboxClick } =
    useSelectedItemsValidation();
  const [editableNoteId, setEditableNoteId] = useState<string>('');
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const navigate = useNavigate();
  const { salonId, planId, completed } = useParams();
  const { showMessage } = useNotification();

  const [fieldsValues, setFieldsValues] = useState<IFieldValues>({});

  const backButtonHandler = () => {
    if (isPreviewMode) {
      completed === 'completed'
        ? navigate(generatePath(URLs.Salon, { salonId }))
        : setIsPreviewMode(false);
    } else {
      currentTab !== 0 ? setCurrentTab(currentTab - 1) : navigate(-1);
    }
  };

  const {
    isLoading: isSalonDataLoading,
    salon,
    actionPlan,
    getActionPlan,
    fetchSalon,
    updateActionPlan,
  } = useSalonStore();
  const { isLoading, initiatives, fetchInitiatives, modifyInitiative } = useInitiativesStore();

  useEffect(() => scrollTo(0, 0), [isPreviewMode]);

  useEffect(() => {
    completed === 'completed' && setIsPreviewMode(true);
  }, []);

  useEffect(() => {
    if (salon) fetchInitiatives(Object.keys(salon?.mCountry)?.[0], showMessage, navigate);
  }, [salon]);

  useEffect(() => {
    if (salonId && !salon) {
      fetchSalon(salonId, showMessage, navigate);
    }
  }, [salon, salonId]);

  useEffect(() => {
    if (planId) {
      getActionPlan(planId, showMessage, navigate);
    }
  }, [planId]);

  useEffect(() => {
    actionPlan?.initiatives?.map((initiative) =>
      modifyInitiative(initiative.id, {
        initiativeDueDate: initiative.initiativeDueDate || null,
        initiativeNote: initiative.initiativeNote || null,
      }),
    );
  }, [actionPlan?.initiatives]);

  const planTabs = useMemo(() => {
    const tabs = [];
    actionPlan?.guestPlan?.kpi.length !== 0 && tabs.push('guestPlan');
    actionPlan?.teamPlan?.kpi.length !== 0 && tabs.push('teamPlan');
    actionPlan?.higherTicketsPlan?.kpi.length !== 0 && tabs.push('higherTicketsPlan');

    return tabs;
  }, [actionPlan]);

  const updatePlan = async () => {
    const preparedInitiatives = Object.values(selectedItems)
      .flat()
      .map((id) => {
        const initiative = initiatives.find((item) => item.id === id);
        return {
          id: id,
          initiativeNote: initiative.initiativeNote || null,
          initiativeDueDate: initiative.initiativeDueDate || null,
        };
      });

    const payload: SalonActionPlan = {
      salonDetailPage: salonId,
      salonKpi: actionPlan?.salonKpi,
      year: actionPlan?.year,
      quarter: actionPlan?.quarter,
      guestPlan: {
        kpi: Object.keys(actionPlan?.guestPlan.kpi),
        plannedGuestCount: {
          m1: (fieldsValues.plannedGuestCountm1 as number) || null,
          m2: (fieldsValues.plannedGuestCountm2 as number) || null,
          m3: (fieldsValues.plannedGuestCountm3 as number) || null,
        },
      },
      teamPlan: {
        kpi: Object.keys(actionPlan?.teamPlan.kpi),
        plannedSpCount: {
          m1: (fieldsValues.plannedSpCountm1 as number) || null,
          m2: (fieldsValues.plannedSpCountm2 as number) || null,
          m3: (fieldsValues.plannedSpCountm3 as number) || null,
        },
      },
      higherTicketsPlan: {
        kpi: Object.keys(actionPlan?.higherTicketsPlan.kpi),
        plannedHigherTicketsCount: {
          m1: (fieldsValues.plannedHigherTicketsCountm1 as number) || null,
          m2: (fieldsValues.plannedHigherTicketsCountm2 as number) || null,
          m3: (fieldsValues.plannedHigherTicketsCountm3 as number) || null,
        },
        plannedRetailTicketsCount: {
          m1: (fieldsValues.plannedRetailTicketsCountm1 as number) || null,
          m2: (fieldsValues.plannedRetailTicketsCountm2 as number) || null,
          m3: (fieldsValues.plannedRetailTicketsCountm3 as number) || null,
        },
      },
      initiatives: preparedInitiatives,
    };

    await updateActionPlan(payload, planId, showMessage, navigate);
  };

  const nextBtnAction = () => {
    if (currentTab < planTabs.length - 1) {
      setCurrentTab(currentTab + 1);
      updatePlan();
    } else {
      setIsPreviewMode(true);
      setCurrentTab(0);
      updatePlan();
    }
  };

  const handleSendButton = () => {
    setIsSendModalOpen(true);
    updatePlan();
  };

  const [currentTab, setCurrentTab] = useState(0);

  const rightButtons: IControlButton[] = [
    { label: t('back'), action: backButtonHandler },
    {
      label: !isPreviewMode ? (
        t('next')
      ) : (
        <>
          <SendIcon /> {t('saveSend')}
        </>
      ),
      action: () => (!isPreviewMode ? nextBtnAction() : handleSendButton()),
      variant: 'contained',
      disabled: !!completed || (!isMinSelected(planTabs[currentTab]) && !isPreviewMode),
      dataTestId: 'save-and-send',
    },
  ];

  const tabProps = (name: keyof typeof PlanTabsTitle) => ({
    label: PlanTabsTitle[name],
    id: `tab-${name}`,
    'aria-controls': `tabpanel-${name}`,
  });

  const handleTabChange = (_: SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  const salonDataTextPrepare = (data: KpiItemValues) => {
    if (typeof data.value === 'string') {
      return (SalonKpiValues as { [key: string]: string })[data.value];
    }
    if (data.value === null) return 'N/A';

    return `${data.prefix ? data.prefix + ' ' : ''}${data.value}${data.suffix || ''}`;
  };

  const kpiTextPrepare = (value: BenchmarkItemValues | string) => {
    if (typeof value === 'string') {
      return (KpiValues as { [key: string]: string })[value];
    }
    return `${value.prefix ? value.prefix + ' ' : ''}${value.minValue}${value.suffix || ''}`;
  };

  const rowsPrepare = (rows: Record<string, string | ActionGuestPlan>[]) => {
    return Object.entries(rows).map((row) => {
      const planData = row[1] as unknown as ActionKPI;
      const kpi = row[1].kpi as unknown as KpiItemValues;

      return {
        kpi: KpiTitle[row[0] as keyof typeof KpiTitle],
        avedaSalonData: salonDataTextPrepare(kpi as unknown as KpiItemValues),
        globalBenchmarkKpi: kpiTextPrepare(
          planData.globalBenchmark as unknown as BenchmarkItemValues,
        ),
        status: (
          <Stack>
            {kpi?.status && <KpiBadge result={kpi.status} />}
            <Typography>{KpiStatus[kpi?.status as keyof typeof KpiStatus] || 'N/A'}</Typography>
          </Stack>
        ),
      };
    });
  };

  const rowInitiative = (rows: Initiative[]) => {
    const filteredTabRows =
      rows?.filter((row) => {
        return row.actionPlanType === convertToSnakeCase(planTabs[currentTab]);
      }) || [];
    return filteredTabRows.map((row) => {
      //if (row.groupRow === 'true') return row;

      const { id, initiativeType, priority, initiativeDueDate, title, initiativeHyperlink } = row;
      const isSelected = selectedItems[planTabs[currentTab]]?.includes(id);
      const isGroupRow = false; //groupRow === 'true';

      return {
        ...row,
        title: initiativeHyperlink ? (
          <StyledLink href={initiativeHyperlink} target="_blank">
            {title}
          </StyledLink>
        ) : (
          <Typography>{title}</Typography>
        ),
        initiativeType: isGroupRow ? (
          <StyledTypeCell fontWeight={initiativeDueDate === 'false' ? 'bold' : 'normal'}>
            {initiativeType}
          </StyledTypeCell>
        ) : (
          <Typography>{initiativeType}</Typography>
        ),
        priority: (
          <Stack>
            <Typography textTransform="capitalize">{priority}</Typography>
          </Stack>
        ),
        initiativeNote: !isGroupRow && (
          <StyledNoticeStack spacing={2}>
            {editableNoteId !== id && (
              <StyledNotes
                title={row.initiativeNote}
                onClick={() => !isPreviewMode && isSelected && setEditableNoteId(id)}
              >
                {row.initiativeNote || `${t('notes')}...`}
              </StyledNotes>
            )}
            {editableNoteId === id && isSelected && (
              <TextField
                onBlur={(e) => {
                  setEditableNoteId('');
                  modifyInitiative(id, { initiativeNote: (e.target as HTMLInputElement).value });
                  row.initiativeNote = (e.target as HTMLInputElement).value;
                }}
                id={`notes_${id}`}
                variant="standard"
                autoFocus
              />
            )}
            {!isPreviewMode && (
              <Checkbox
                checked={!!isSelected}
                onChange={() => {
                  !isSelected && isMaxSelected(planTabs[currentTab])
                    ? showMessage(t('notifications.maxInitiatives'), 'success')
                    : onCheckboxClick(id, planTabs[currentTab]);
                }}
                id={id}
              />
            )}
          </StyledNoticeStack>
        ),
        initiativeDueDate: !isGroupRow && (
          <DueDatePicker
            date={initiativeDueDate ? dayjs(initiativeDueDate) : null}
            disabled={isPreviewMode || !isSelected}
            setDate={(date) =>
              modifyInitiative(id, { initiativeDueDate: date.format('YYYY-MM-DD') })
            }
          />
        ),
      };
    });
  };

  const getRowsForPreviewMode = (rows: Initiative[]): Initiative[] => {
    const selectedRows = rows.filter((row) =>
      selectedItems[planTabs[currentTab]]?.includes(row.id),
    );
    const titleRowsIds = [...new Set(selectedRows.map((item) => item.resource))];
    const selectedRowsWithTitlesIds = selectedItems[planTabs[currentTab]]
      ? [...selectedItems[planTabs[currentTab]], ...titleRowsIds]
      : [];

    return rows.filter((row) => selectedRowsWithTitlesIds.includes(row.id));
  };

  const labelPrepare = (isActualPlan: boolean, index: number) => {
    if (isActualPlan) {
      return `${t(`months.${quarterMonths[Number(actionPlan?.quarter) - 1][index]}`)} ${actionPlan?.year}`;
    } else if (actionPlan?.quarter === '4') {
      return `${t(`months.${quarterMonths[0][index]}`)} ${Number(actionPlan?.year) + 1}`;
    } else {
      return `${t(`months.${quarterMonths[Number(actionPlan?.quarter)][index]}`)} ${actionPlan?.year}`;
    }
  };

  const fieldPrepare = (fields: ActionPlanCount, fieldsTitle: string, isActualPlan: boolean) =>
    Object.entries(fields).map((field, index) => ({
      label: labelPrepare(isActualPlan, index),
      id: fieldsTitle + field[0],
      name: field[0],
      value: fieldsValues[fieldsTitle + field[0]] || field[1],
      disabled: isActualPlan,
      cellSize: 4,
      helperText: 'Some important text',
    }));

  const complitedFieldsPrepare = (values: Array<string>[], groupName: string) => [
    {
      [groupName + values[0][0]]: values[0][1] || '-',
      [groupName + values[1][0]]: values[1][1] || '-',
      [groupName + values[2][0]]: values[2][1] || '-',
    },
  ];

  return (
    <>
      <ControlBar
        backButtonAction={() => navigate(generatePath(URLs.Salon, { salonId }))}
        title={<Typography variant="h5">{actionPlan?.title || t('newPlan')}</Typography>}
        withKpiButton
      />
      <Loader isLoading={isLoading || isSalonDataLoading} />
      <Container>
        <Tabs
          textColor="secondary"
          indicatorColor="secondary"
          value={currentTab}
          onChange={handleTabChange}
          aria-label="Plans"
        >
          {planTabs.map((plan, index) => (
            <Tab key={`index_${index}`} {...tabProps(plan as keyof typeof PlanTabsTitle)} />
          ))}
        </Tabs>
      </Container>
      <Divider />
      {planTabs.map((plan, index) => (
        <TabPanel key={`index_${index}`} value={currentTab} index={index}>
          {isPreviewMode && (
            <PreviewTitleBox>
              <Typography variant="h4" textTransform="capitalize">
                {actionPlan?.title}
              </Typography>
            </PreviewTitleBox>
          )}
          {actionPlan &&
            Object.entries(actionPlan?.[plan as keyof SalonActionPlan])
              .sort((a, b) => blocksOrder[a[0]] - blocksOrder[b[0]])
              .map((item, index) => {
                const isActualField =
                  item[0].includes('actual') || item[0].includes('Transactions');

                return item[0].includes('kpi') ? (
                  <Box mt={6} gap={6}>
                    <TableComponent
                      rows={rowsPrepare(item[1])}
                      columns={LocalizedColumns(KpiColumns, t, true)}
                      dataTestId="selected-kpi-table"
                    />
                  </Box>
                ) : !isPreviewMode ? (
                  <FormGrid
                    onChangeFieldValues={(values) =>
                      setFieldsValues((prevState) => ({ ...prevState, ...values }))
                    }
                    key={item[0] + index}
                    formDescription={
                      ActionPlanGroupsTitleDescription[
                        item[0] as keyof typeof ActionPlanGroupsTitleDescription
                      ]
                    }
                    formName={ActionPlanGroupsTitle[item[0] as keyof typeof ActionPlanGroupsTitle]}
                    fields={fieldPrepare(item[1], item[0], isActualField)}
                  />
                ) : (
                  <Box key={item[0] + index} mt={6} gap={6}>
                    <Typography variant="h6" mb={2}>
                      <FormItemLabel
                        label={ActionPlanGroupsTitle[item[0] as keyof typeof ActionPlanGroupsTitle]}
                        description={false}
                      />
                    </Typography>
                    <TableComponent
                      rows={
                        completed === 'completed'
                          ? complitedFieldsPrepare(Object.entries(item[1]), item[0])
                          : [fieldsValues]
                      }
                      columns={Object.keys(item[1]).map((title, index) => ({
                        id: item[0] + title,
                        name: labelPrepare(isActualField, index),
                      }))}
                      dataTestId={`preview-${item[0].toLowerCase().replaceAll(' ', '-')}-table`}
                    />
                  </Box>
                );
              })}
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box mt={6}>
              <Typography variant="h6">{t('initiatives')}</Typography>
              <Typography mb={2}>{t('selectInitiative')}</Typography>
              <TableComponent
                rows={rowInitiative(
                  isPreviewMode
                    ? completed === 'completed'
                      ? actionPlan?.initiatives
                      : getRowsForPreviewMode(initiatives)
                    : initiatives,
                )}
                columns={LocalizedColumns(InitiativeColumns, t)}
                dataTestId="initiatives-table"
              />
            </Box>
          </LocalizationProvider>
        </TabPanel>
      ))}
      <SendFormModal
        plans={planTabs}
        open={isSendModalOpen}
        handleClose={() => {
          setIsSendModalOpen(false);
        }}
      />
      <ControlBar rightButtons={rightButtons} />
    </>
  );
};
